/**
 * Reader Profile Page - Identity & Authority Surface
 * 
 * PURPOSE: Source of truth for WHO the reader is, not what they've spent.
 * 
 * SECTIONS:
 * A) Registration & Contact (name, email, userID, password change)
 * B) Roles & Permissions (inline read-only display)
 * C) Membership & Subscription (display + cancel/upgrade actions)
 * D) Payment Method Management (add/replace/remove card with modal)
 * E) Profile Image Upload (avatar)
 * 
 * EXPLICIT EXCLUSIONS:
 * - No credit balances
 * - No transaction history
 * - No consumption data
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useReaderFunnel } from '../../contexts/ReaderFunnelContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { 
  User, Mail, Shield, Phone, MapPin, Lock, Eye, EyeOff, 
  Crown, CreditCard, Calendar, RefreshCw, ChevronRight,
  Camera, Upload, Check, X, Loader2, AlertCircle, ArrowUp
} from 'lucide-react';
import axios from 'axios';
import AccountNav from '../../components/AccountNav';
import UniversalFooter from '../../components/UniversalFooter';
import CardCaptureModal from '../../components/CardCaptureModal';
import PaymentMethodRequiredModal from '../../components/PaymentMethodRequiredModal';
import { useIsReaderRole } from '../../hooks/useIsReaderRole';
import { xhrFetchAuth } from '../../lib/xhrFetch';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  // Format based on length
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    // Handle numbers with country code 1
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  // Return as-is if not a standard US number
  return phone;
};

// Format phone as user types (for input field)
const formatPhoneInput = (value) => {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, getHomePage, loadCurrentUser } = useAuth();
  const { setShowPricingModal } = useReaderFunnel();
  const { notifyError, notifyBilling, notifyAccount } = useNotification();
  const isReader = useIsReaderRole();
  
  // Author/Publisher role detection
  const userRoles = user?.roles || [];
  const isAuthorOrPublisher = userRoles.includes('author') || userRoles.includes('publisher') || userRoles.includes('creator');
  
  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Subscription data state
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(true);
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Avatar upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  
  // Password change state
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  // Payment method modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Author/Publisher payment method state
  const [authorPaymentMethod, setAuthorPaymentMethod] = useState(null);
  const [loadingAuthorPayment, setLoadingAuthorPayment] = useState(true);
  const [showAuthorPaymentModal, setShowAuthorPaymentModal] = useState(false);
  
  // Form data state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    alternate_email: '',
    city: '',
    state: '',
    country: ''
  });

  const userId = user?.user_id || user?.id;

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        
        const response = await axios.get(`${API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setProfileData(response.data);
        setFormData({
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          phone: formatPhoneNumber(response.data.phone || ''),
          alternate_email: response.data.alternate_email || '',
          city: response.data.city || '',
          state: response.data.state || '',
          country: response.data.country || ''
        });
        setAvatarPreview(response.data.avatar_url || null);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        notifyError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfileData();
  }, []);

  // Fetch subscription data
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!userId) return;
      
      try {
        const response = await fetch(
          `${API_URL}/api/nfm/subscription/my-subscription?user_id=${userId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    };
    
    fetchSubscription();
  }, [userId]);

  // Fetch payment method from billing profile status
  useEffect(() => {
    const fetchPaymentMethod = async () => {
      if (!userId) return;
      
      try {
        const response = await fetch(
          `${API_URL}/api/nfm/billing/profile/status?user_id=${userId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          // Map billing profile response to payment method format
          setPaymentMethod({
            has_payment_method: data.has_payment_method,
            card_brand: data.payment_method_brand,
            card_last4: data.payment_method_last4,
            card_exp_month: null,
            card_exp_year: null
          });
        }
      } catch (error) {
        console.error('Error fetching payment method:', error);
      } finally {
        setLoadingPayment(false);
      }
    };
    
    fetchPaymentMethod();
  }, [userId]);

  // Fetch Author/Publisher payment method status from users collection
  useEffect(() => {
    const fetchAuthorPaymentMethod = async () => {
      if (!isAuthorOrPublisher) {
        setLoadingAuthorPayment(false);
        return;
      }
      
      try {
        const res = await xhrFetchAuth(`${API_URL}/api/payments/method-status`);
        if (res.ok && res.json) {
          setAuthorPaymentMethod(res.json);
        }
      } catch (error) {
        console.error('Error fetching author payment method:', error);
      } finally {
        setLoadingAuthorPayment(false);
      }
    };
    
    fetchAuthorPaymentMethod();
  }, [isAuthorOrPublisher]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    // Format phone number as user types
    if (field === 'phone') {
      value = formatPhoneInput(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle password changes
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Save profile
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        notifyAccount('Not authenticated. Please log in again.');
        navigate('/login');
        return;
      }

      console.log('[PROFILE] Saving profile data:', formData);
      
      const response = await axios.put(
        `${API_URL}/api/auth/update-profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[PROFILE] Save response:', response.data);

      // Handle password change if requested
      if (changingPassword && passwordData.new_password) {
        if (passwordData.new_password !== passwordData.confirm_password) {
          notifyError('New passwords do not match');
          setSaving(false);
          return;
        }
        
        if (passwordData.new_password.length < 8) {
          notifyError('Password must be at least 8 characters');
          setSaving(false);
          return;
        }

        await axios.post(
          `${API_URL}/api/auth/change-password`,
          {
            current_password: passwordData.current_password,
            new_password: passwordData.new_password
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setChangingPassword(false);
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        notifyAccount('Password changed successfully!');
      }

      // Update local state with saved data
      setProfileData(response.data);
      
      // Show success notification
      notifyAccount('Profile saved successfully!');
      
      // Navigate to Author homepage immediately
      navigate(getHomePage(user));
      
    } catch (error) {
      console.error('[PROFILE] Error updating profile:', error);
      console.error('[PROFILE] Error response:', error.response?.data);
      notifyError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel auto-renew
  const handleCancelAutoRenew = async () => {
    try {
      setCancelling(true);
      const response = await fetch(
        `${API_URL}/api/nfm/subscription/cancel-auto-renew`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        }
      );

      if (response.ok) {
        const result = await response.json();
        notifyBilling(result.message, 'Subscription Updated');
        setCancelDialogOpen(false);
        // Refresh subscription data
        const subResponse = await fetch(
          `${API_URL}/api/nfm/subscription/my-subscription?user_id=${userId}`
        );
        if (subResponse.ok) {
          setSubscription(await subResponse.json());
        }
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling:', error);
      notifyError('Unable to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      notifyError('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notifyError('Image must be less than 5MB');
      return;
    }

    try {
      setUploadingAvatar(true);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to backend
      const formData = new FormData();
      formData.append('avatar', file);
      formData.append('user_id', userId);

      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/auth/upload-avatar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.avatar_url) {
        setAvatarPreview(response.data.avatar_url);
        // Avatar visible in UI - no toast needed
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      notifyError('Failed to upload avatar');
      // Revert preview on error
      setAvatarPreview(profileData?.avatar_url || null);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '—';
    }
  };

  // Format date as "Month Year" (for membership since)
  const formatMonthYear = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return '—';
    }
  };

  // Get full name
  const getFullName = () => {
    const firstName = formData.first_name || profileData?.first_name || '';
    const lastName = formData.last_name || profileData?.last_name || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return user?.name || user?.email || 'Reader';
  };

  // Get monthly credits display (policy: annual = total/12)
  const getMonthlyCredits = (creditsPerPeriod, cadence) => {
    if (cadence === 'annual') {
      return Math.floor(creditsPerPeriod / 12);
    }
    return creditsPerPeriod;
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Handle payment method addition - opens the CardCaptureModal
  const handleAddPaymentMethod = () => {
    setShowPaymentModal(true);
  };

  // Handle successful card capture from CardCaptureModal
  const handleCardCaptureSuccess = async (result) => {
    console.log('[PROFILE] Card captured successfully:', result);
    setShowPaymentModal(false);
    
    // Refresh payment method data
    try {
      const response = await fetch(
        `${API_URL}/api/nfm/billing/profile/status?user_id=${userId}`
      );
      if (response.ok) {
        const data = await response.json();
        setPaymentMethod({
          has_payment_method: data.has_payment_method,
          card_brand: data.payment_method_brand,
          card_last4: data.payment_method_last4,
          card_exp_month: null,
          card_exp_year: null
        });
      }
    } catch (error) {
      console.error('[PROFILE] Error refreshing payment method:', error);
    }
    
    // Refresh user data (membership might have changed to A La Carte)
    loadCurrentUser?.();
    
    notifyBilling('Payment method added successfully!');
  };

  // Handle successful Author/Publisher payment method capture
  const handleAuthorPaymentSuccess = async (result) => {
    console.log('[PROFILE] Author/Publisher payment method saved:', result);
    setShowAuthorPaymentModal(false);
    
    // Update local state with the returned card info
    setAuthorPaymentMethod({
      has_payment_method: true,
      brand: result.brand,
      last4: result.last4
    });
    
    notifyBilling('Payment method added successfully!');
  };

  // Check for payment success callback from redirect (legacy support)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment_added') === 'true') {
      notifyBilling('Payment method added successfully!');
      // Refresh payment method data
      const fetchPaymentMethod = async () => {
        try {
          const response = await fetch(
            `${API_URL}/api/nfm/billing/profile/status?user_id=${userId}`
          );
          if (response.ok) {
            const data = await response.json();
            setPaymentMethod({
              has_payment_method: data.has_payment_method,
              card_brand: data.payment_method_brand,
              card_last4: data.payment_method_last4,
              card_exp_month: null,
              card_exp_year: null
            });
          }
        } catch (error) {
          console.error('Error refreshing payment method:', error);
        }
      };
      fetchPaymentMethod();
      // Refresh user data (membership might have changed to A La Carte)
      loadCurrentUser?.();
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [userId, loadCurrentUser]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
        <AccountNav />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <AccountNav />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header with Reader Profile Lead */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-1">
            Reader Profile for - {getFullName()}
          </h1>
          <p className="text-slate-400 text-lg">
            Valued NewAudia Reader since: {formatMonthYear(profileData?.created_at || user?.created_at)}
          </p>
          
          {/* Membership Type and Roles */}
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            {/* Membership Type */}
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-slate-500 text-sm">Membership:</span>
              <Badge
                className={`${
                  (profileData?.membership_type || user?.membership_type) === 'Subscriber'
                    ? 'bg-purple-500/20 text-purple-300 border-purple-400/40'
                    : (profileData?.membership_type || user?.membership_type) === 'ALaCarte'
                    ? 'bg-amber-500/20 text-amber-300 border-amber-400/40'
                    : 'bg-slate-500/20 text-slate-300 border-slate-400/40'
                }`}
              >
                {(profileData?.membership_type || user?.membership_type) === 'ALaCarte' 
                  ? 'A La Carte' 
                  : (profileData?.membership_type || user?.membership_type) || 'Free'}
              </Badge>
            </div>
            
            {/* Roles & Permissions */}
            {user?.roles && user.roles.length > 0 && (
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-amber-400" />
                <span className="text-slate-500 text-sm">Roles:</span>
                {user.roles.map(role => (
                  <Badge
                    key={role}
                    className="bg-indigo-500/20 text-indigo-300 border-indigo-400/40"
                  >
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Account Navigation Tabs - P0.5: Only show RCM tabs for Readers */}
        <div className="mb-6 flex gap-2 border-b border-slate-700 pb-4">
          <Link 
            to="/account/profile"
            className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 font-medium"
          >
            Profile
          </Link>
          {/* Reader Credits and Billing tabs - Reader-only */}
          {isReader && (
            <>
              <Link 
                to="/account/credits"
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
              >
                Reader Credits
              </Link>
              <Link 
                to="/account/billing"
                className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
              >
                Billing
              </Link>
            </>
          )}
          <Link 
            to={getHomePage(user)}
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            {isReader ? 'Reader Home' : 'Home'}
          </Link>
        </div>

        <div className="space-y-6">
          {/* Section A: Identity & Contact */}
          <Card className="bg-slate-800/50 border-slate-700" data-testid="identity-section">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Identity & Contact
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar + Name Row */}
              <div className="flex items-start gap-6">
                {/* Avatar Upload */}
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden border-2 border-slate-600">
                    {avatarPreview ? (
                      <img 
                        src={avatarPreview.startsWith('http') ? avatarPreview : `${API_URL}${avatarPreview}`} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-3xl font-bold text-slate-400">
                        {(formData.first_name?.[0] || user?.name?.[0] || 'U').toUpperCase()}
                      </span>
                    )}
                  </div>
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleAvatarUpload}
                      disabled={uploadingAvatar}
                    />
                    {uploadingAvatar ? (
                      <Loader2 className="w-6 h-6 text-white animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-white" />
                    )}
                  </label>
                </div>

                {/* Name Fields */}
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      First Name
                    </label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-slate-100"
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Last Name
                    </label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className="bg-slate-700/50 border-slate-600 text-slate-100"
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Mail className="inline w-4 h-4 mr-2" />
                  Primary Email (User ID)
                </label>
                <div className="p-3 bg-slate-700/30 rounded-lg text-slate-400 border border-slate-600">
                  {user?.email}
                  <span className="ml-2 text-xs text-slate-500">(cannot be changed)</span>
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Phone className="inline w-4 h-4 mr-2" />
                    Phone
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-slate-100"
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    <Mail className="inline w-4 h-4 mr-2" />
                    Alternate Email
                  </label>
                  <Input
                    type="email"
                    value={formData.alternate_email}
                    onChange={(e) => handleInputChange('alternate_email', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-slate-100"
                    placeholder="Enter alternate email"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <MapPin className="inline w-4 h-4 mr-2" />
                  Location
                </label>
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-slate-100"
                    placeholder="City"
                  />
                  <Input
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-slate-100"
                    placeholder="State/Province"
                  />
                  <Input
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-slate-100"
                    placeholder="Country"
                  />
                </div>
              </div>

              {/* Password Change Section */}
              {changingPassword && (
                <div className="border-t border-slate-700 pt-6 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-medium text-slate-300">Change Password</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.current_password}
                        onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-slate-100 pr-10"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-slate-100 pr-10"
                        placeholder="Enter new password (min 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                        className="bg-slate-700/50 border-slate-600 text-slate-100 pr-10"
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setChangingPassword(false);
                      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    }}
                    variant="outline"
                    size="sm"
                    className="bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600"
                  >
                    Cancel Password Change
                  </Button>
                </div>
              )}

              {/* Change Password Button (when not changing) */}
              {!changingPassword && (
                <div className="border-t border-slate-700 pt-4 flex justify-end">
                  <Button
                    onClick={() => setChangingPassword(true)}
                    variant="outline"
                    size="sm"
                    className="bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section C: Membership & Subscription - READER ONLY */}
          {/* P0.5: RCM surfaces are structurally excluded for non-Reader roles */}
          {isReader && (
          <Card className="bg-slate-800/50 border-slate-700" data-testid="subscription-section">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Membership & Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSubscription ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : !subscription?.has_subscription ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-slate-700 flex items-center justify-center mb-4">
                    <CreditCard className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {profileData?.membership_type === 'ALaCarte' ? 'A La Carte' : profileData?.membership_type === 'Introductory' ? 'Introductory' : 'Free Tier'}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    Subscribe to get monthly credits and save on your audiobook listening.
                  </p>
                  <Button 
                    onClick={() => setShowPricingModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  >
                    Continue Your NewAudia Journey
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Subscription Overview */}
                  <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl font-bold text-white">
                          {subscription.tier_display}
                        </span>
                        <Badge className={
                          subscription.status === 'active' 
                            ? 'bg-green-500/20 text-green-400 border-green-400/40'
                            : 'bg-yellow-500/20 text-yellow-400 border-yellow-400/40'
                        }>
                          {subscription.status_display}
                        </Badge>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">
                        {subscription.cadence_display} billing • {subscription.price_display}/{subscription.cadence === 'monthly' ? 'month' : 'year'}
                      </p>
                    </div>
                    {subscription.auto_renew && (
                      <div className="flex items-center gap-1 text-green-400 text-sm">
                        <RefreshCw className="w-4 h-4" />
                        Auto-renews
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                      <div className="text-sm text-slate-400">Credits/Month</div>
                      <div className="text-xl font-bold text-amber-400">
                        {getMonthlyCredits(subscription.credits_per_period, subscription.cadence)}
                      </div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                      <div className="text-sm text-slate-400">Days Left</div>
                      <div className="text-xl font-bold text-white">
                        {subscription.days_remaining}
                      </div>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                      <div className="text-sm text-slate-400">
                        {subscription.auto_renew ? 'Renews' : 'Access Until'}
                      </div>
                      <div className="text-sm font-semibold text-white">
                        {formatDate(subscription.end_date)}
                      </div>
                    </div>
                  </div>

                  {/* Canceled Notice */}
                  {subscription.status === 'canceled' && (
                    <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-yellow-300 font-medium">Subscription Canceled</div>
                        <div className="text-yellow-400/80 text-sm">
                          Your subscription will not renew. You&apos;ll continue to have access until {formatDate(subscription.end_date)}.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    {subscription.can_upgrade && subscription.upgrade_options?.length > 0 && (
                      <Button 
                        onClick={() => navigate('/pricing?upgrade=true')}
                        className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                      >
                        <ArrowUp className="w-4 h-4 mr-2" />
                        Upgrade Plan
                      </Button>
                    )}
                    {subscription.can_cancel && (
                      <Button 
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-900/20"
                        onClick={() => setCancelDialogOpen(true)}
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Auto-Renewal
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Section D: Payment Method - READER ONLY */}
          {/* P0.5: Payment method management is part of RCM economy, hidden for non-Readers */}
          {isReader && (
          <Card className="bg-slate-800/50 border-slate-700" data-testid="payment-section">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-400" />
                Payment Method
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingPayment ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : paymentMethod?.has_payment_method ? (
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 bg-slate-600 rounded flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium capitalize">
                        {paymentMethod.card_brand} •••• {paymentMethod.card_last4}
                      </div>
                      {paymentMethod.card_exp_month && paymentMethod.card_exp_year && (
                        <div className="text-slate-400 text-sm">
                          Expires {paymentMethod.card_exp_month}/{paymentMethod.card_exp_year}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="text-amber-400 hover:text-amber-300"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    Update
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-slate-400 mb-3">No payment method on file</p>
                  <Button 
                    onClick={() => setShowPaymentModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                  >
                    Add Payment Method
                  </Button>
                  <p className="text-xs text-slate-500 mt-2">
                    Adding a payment method will upgrade you to A La Carte pricing
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Section D-Alt: Payment Method - AUTHOR/PUBLISHER ONLY */}
          {isAuthorOrPublisher && (
          <Card className="bg-slate-800/50 border-slate-700" data-testid="author-payment-section">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-400" />
                Payment Method
              </CardTitle>
              <CardDescription className="text-slate-400">
                Card on file for manuscript activation fees
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAuthorPayment ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : authorPaymentMethod?.has_payment_method ? (
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 bg-slate-600 rounded flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <div className="text-white font-medium capitalize" data-testid="author-card-display">
                        {authorPaymentMethod.brand} •••• {authorPaymentMethod.last4}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    className="text-amber-400 hover:text-amber-300"
                    onClick={() => setShowAuthorPaymentModal(true)}
                    data-testid="update-author-payment-btn"
                  >
                    Update Payment Method
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-slate-400 mb-3">No payment method on file.</p>
                  <Button 
                    onClick={() => setShowAuthorPaymentModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                    data-testid="add-author-payment-btn"
                  >
                    Add Payment Method
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={() => navigate(getHomePage(user))}
              variant="outline"
              className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveProfile}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>

      <UniversalFooter />

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          {/* NewAudia Logo */}
          <div className="flex justify-center pt-2 pb-4">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-12 w-auto rounded"
            />
          </div>
          
          <DialogHeader>
            <DialogTitle className="text-white text-center">Cancel Auto-Renewal?</DialogTitle>
            <DialogDescription className="text-slate-400 text-center">
              Your subscription will not renew at the end of the current billing period.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-4 h-4" />
                <span className="text-sm">You&apos;ll keep access until {formatDate(subscription?.end_date)}</span>
              </div>
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-4 h-4" />
                <span className="text-sm">Your Reader Credits will remain available</span>
              </div>
              <div className="flex items-center gap-2 text-green-400">
                <Check className="w-4 h-4" />
                <span className="text-sm">You can resubscribe at any time</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => setCancelDialogOpen(false)}
              className="text-slate-300"
            >
              Keep Subscription
            </Button>
            <Button 
              onClick={handleCancelAutoRenew}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                'Cancel Auto-Renewal'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Capture Modal (Reader) */}
      <CardCaptureModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handleCardCaptureSuccess}
        userId={userId}
        userEmail={user?.email}
        userName={user?.name || `${formData.first_name || ''} ${formData.last_name || ''}`.trim()}
        token={localStorage.getItem('accessToken')}
        membershipType={profileData?.membership_type || 'Free'}
      />

      {/* Payment Method Required Modal (Author/Publisher) */}
      <PaymentMethodRequiredModal
        isOpen={showAuthorPaymentModal}
        onSuccess={handleAuthorPaymentSuccess}
        onDismiss={() => setShowAuthorPaymentModal(false)}
      />
    </div>
  );
};

export default ProfilePage;
